"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  target,
  suffix = ""
}: {
  target: number;
  suffix?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number;
    const duration = 2000;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * target));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, target]);
  return <span ref={ref} className="tabular-nums">
      {count}
      {suffix}
    </span>;
}
const statsData = [{
  label: "Superior Projects",
  value: 500,
  suffix: "+",
  description: "Homes restored & protected"
}, {
  label: "Industry Expertise",
  value: 15,
  suffix: "+",
  description: "Years of master craftsmanship"
}, {
  label: "Client Satisfaction",
  value: 100,
  suffix: "%",
  description: "Local community approval"
}];
export default function MeasurableInclusion() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true
  });
  return <section data-section-id="224145" id="why-local-homeowners-trust-us" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-4">
            Proven Excellence in Every Build
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-default">
            Our numbers reflect a legacy of technical precision and unwavering 
            commitment to the homeowners of our local community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {statsData.map((stat, i) => <div key={i} data-index={i} className={`transition-all duration-1000 delay-${i * 200} transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              {}
              <Card className="border border-border/40 shadow-xl bg-card/60 backdrop-blur-md text-foreground h-full flex flex-col justify-center items-center text-center p-8 rounded-md hover:scale-105 transition-transform duration-500">
                <CardContent className="p-0 flex flex-col items-center gap-4">
                  <div className="font-mono text-6xl lg:text-7xl font-bold tracking-tighter text-primary">
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-default text-xl font-bold leading-tight tracking-tight">
                      {stat.label}
                    </h3>
                    <p className="text-muted-foreground text-xs font-mono uppercase tracking-widest">
                      {stat.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}